<script>
  import Chart from 'chart.js/auto';
  import ChartDataLabels from 'chartjs-plugin-datalabels';
  import SummaryGridItem from "../lib/SummaryGridItem.svelte";
  import { afterUpdate, onMount } from 'svelte';
  import OverviewItem from './OverviewItem.svelte';
  import alternativeApi from '../common/alternativeApi';
  import { SupportedLanguages, getLanguageInfo } from '../common/localization';

  Chart.register(ChartDataLabels);

  export let language = SupportedLanguages.English;
  export let chart = null;
  export let fearAndGreedSummary = {};
  export let btcSummary = {};

  let canvasRef = null;

  const languageInfo = getLanguageInfo(language);
  const currencyFormatter = Intl.NumberFormat(languageInfo.locale, { style: "currency", currency: languageInfo.currency });

  onMount(() => {
    fetchFearAndGreedPriceData();
    fetchSummaryData();
    chart = new Chart(canvasRef, {
      type: "doughnut",
      plugins: [{
        id: "chart_meter",
        afterDatasetDraw: function(chart, args, options) {
          const { ctx, chartArea } = chart;
          if (!chartArea)
            return;

          const chartWidth = chartArea.right - chartArea.left;
          const x = chartArea.left + (chartWidth / 2);
          const y = chartArea.bottom - (chartArea.bottom / 4);
          
          const radius = 30;
          ctx.beginPath();
          ctx.lineWidth = 6;
          ctx.arc(x, y, radius, 0, 2 * Math.PI)
          ctx.fillStyle = "white";
          ctx.strokeStyle = "#78CA29";

          ctx.fillStyle = "black"
          if (fearAndGreedSummary.currentValue) {
            ctx.font = "18px Inter";
            ctx.fillText(fearAndGreedSummary.currentValue, x - 12, y + 8);

            const percent = Number.parseFloat(fearAndGreedSummary.currentValue) / 100;
            const radians = (Math.PI * percent) + Math.PI; // value should be between Pi and 2Pi.

            const originX = Math.cos(radians) * radius;
            const originY = Math.sin(radians) * radius;
            ctx.moveTo(x + originX, y + originY); // start the line on the edge of the circle
            ctx.lineTo(x + (2.5 * originX), y + (2.5 * originY)); // draw a line slightly longer than the radius of the circle.
          }

          // Draw the outer axis markers.
          ctx.font = "14px Inter";
          ctx.fillText("0", x - (chartWidth / 3.75), y);

          const twentyFiveRads = (Math.PI * .25) + Math.PI;
          ctx.fillText("25", x + (Math.cos(twentyFiveRads) * (chartWidth/3)) , y + (Math.sin(twentyFiveRads) * (chartWidth/5)));

          ctx.fillText("50", x - 10, y - (chartWidth/4));

          const seventyFiveRads = (Math.PI * .75) + Math.PI;
          ctx.fillText("75", x + (Math.cos(seventyFiveRads) * (chartWidth/3.5)) , y + (Math.sin(seventyFiveRads) * (chartWidth/5)));

          ctx.fillText("100", x + (chartWidth/4.5), y);

          ctx.stroke();
        }
      }],
      options: {
        animation: false,
        circumference: 180,
        rotation: -90,
        borderColor: "transparent",
        cutout: "60%",
        responsive: true,
        plugins: {
          legend: {
            display: false
          },
          tooltip: {
            enabled: false
          },
          datalabels: {
            anchor: "center",
            formatter: function(_, context) {
              switch (context.dataIndex) {
                case 0: return "Extreme\nFear"
                case 1: return "Fear"
                case 2: return "Neutral"
                case 3: return "Greed"
                case 4: return "Extreme\nGreed"
              }
            },
            labels: {
              value: null,
              title: {
                color: "#001A38",
                textAlign: "center",
                font: {
                  size: 12,
                  family: "Inter",
                  weight: 600
                }
              }
            }
          }
        }
      },
      data: {
        labels: ["one", "two", "three", "four", "five"],
        datasets: [{
          data: [20, 20, 20, 20, 20],
          backgroundColor: function({ chart: { ctx, chartArea } }) {
            if (!chartArea)
              return;
            const chartWidth = chartArea.right - chartArea.left;
            const gradient = ctx.createConicGradient(Math.PI, chartArea.left + (chartWidth / 2), chartArea.bottom - (chartArea.bottom / 4));
            gradient.addColorStop(0, "#E52E00");
            gradient.addColorStop(.1, "#E06316")

            gradient.addColorStop(.1, "#E86618");
            gradient.addColorStop(.2, "#F9AB47");

            gradient.addColorStop(.2, "#F9AC48");
            gradient.addColorStop(.3, "#FFDE69");

            gradient.addColorStop(.3, "#F9BD48");
            gradient.addColorStop(.4, "#DAC718");
            
            gradient.addColorStop(.4, "#D9D117");
            gradient.addColorStop(.5, "#3EDB45");

            return gradient;
          }
        }]
      }
    })
  });

  afterUpdate(() => {
    if (!chart) return;

    chart.update();
  });

  const fetchFearAndGreedPriceData = async function() {
    const { values: v, error } = await alternativeApi.getFearAndGreedIndexData();
    if (!error)
      fearAndGreedSummary = v;
  }

  const fetchSummaryData = async function() {
    const { summaryValues } = await alternativeApi.getBitcoinSummaryInfo(languageInfo.currency);
    btcSummary = summaryValues;
  }
</script>

<div class="w-full font-sans">
  <div class="flex flex-col md:flex-row items-center justify-center gap-x-3 gap-y-3 px-3">
    <SummaryGridItem title="Market Cap" value={currencyFormatter.format(Number.parseFloat(btcSummary?.marketCap ?? 0))} />
    <SummaryGridItem title="Volume 24h" value={currencyFormatter.format(Number.parseFloat(btcSummary?.dailyVolume ?? 0))} />
    <SummaryGridItem title="BTC Dominance" value={`${(Number.parseFloat(btcSummary?.dominance ?? 0)).toFixed(2)}%`} />
  </div>

  <div class="relative w-full lg:w-[1238px] max-w-[100vw] flex flex-col lg:flex-row items-center justify-around mt-9 rounded-2xl border border-neutral-300">
    <div class="h-auto md:h-[490px] w-full md:w-[572px]">
      <canvas bind:this={canvasRef}></canvas>
    </div>

    <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-1 gap-x-6 py-6 mb-8">
      {#each fearAndGreedSummary?.overviewValues ?? [] as overviewItem}
        <OverviewItem label={overviewItem.label} classification={overviewItem.classification} value={overviewItem.value} />
      {/each}
    </div>
    <div>

    </div>
    <div class="absolute bottom-4 left-4 text-xs font-normal text-neutral-500">
      Data provided by <a href="https://alternative.me/crypto/fear-and-greed-index/">Alternative</a>
    </div>
  </div>
  
</div>
